import isOnLocalhost from '@client-shared/utils/is-on-localhost'
import raygunSensitiveKeys from '@client-shared/utils/raygun-sensitive-keys.js'
import redactRaygunBreadcrumbs from '@client-shared/utils/redact-raygun-breadcrumbs.js'

import packageJson from '../../package.json'

const normalizeError = (error) => {
  try {
    if (error instanceof Error) {
      return error
    }

    // Report the original Error from browser events so its stack survives.
    const wrappedError = [
      error?.error,
      error?.reason,
      error?.target?.error,
    ].find(value => value instanceof Error)

    return wrappedError ?? new Error(`Non-Error thrown: ${[
      error?.constructor?.name,
      error?.type,
      error?.message,
      String(error),
    ].filter(value => value !== undefined && value !== null)
      .map(String)
      .join(', ')}`)
  } catch {
    // An object whose getters or string conversion throw
    return new Error(`Non-Error thrown: ${typeof error}`)
  }
}

// URL.parse would be shorter, but Safari only has it from 18 on
const getUrlOrigin = (url) => {
  try {
    return new URL(url).origin
  } catch {
    return undefined
  }
}

export default {
  install: (app) => {
    // uncomment this early return
    // and the if statement wrapping the raygun client in client/index.html
    // to enable to raygun logging from localhost to app.raygun.com for testing
    if (!window.rg4js || isOnLocalhost) {
      return
    }

    const $router = app.config.globalProperties.$router

    $router.afterEach((to, from) => {
      window.rg4js('trackEvent', {
        type: 'pageView',
        path: to.path,
      })
    })

    if (isOnLocalhost) {
      console.log('using raygun apiKey for localhost')
      window.rg4js('apiKey', 'h1T78R9XAtw6VBIwi7yXA')
    }

    if (window.location.hostname === 'app.planfred.com' || window.location.hostname === 'demo.planfred.com') {
      window.rg4js('apiKey', 'WhZkDCKmsHbmYjruBaB54g')
    }

    if (window.location.hostname === 'app.planfred.com.planfredapp.com') {
      window.rg4js('apiKey', 'yOGoaGTgobuPCIRLUVqVJA')
    }

    if (window.location.hostname === 'app.apg-planserver.at') {
      window.rg4js('apiKey', 'UllbCCeO3xGNotQElHQw')
    }

    if (window.location.hostname === 'app.koupserver.at') {
      window.rg4js('apiKey', 'jcA2cU7FCB9HdvDRZrGiZg')
    }

    if (window.location.hostname === 'app.egv-planserver.de') {
      window.rg4js('apiKey', 'QPWDkG0nE0TbtOu9txRCA')
    }

    if (window.location.hostname === 'app.klg-planserver.de') {
      window.rg4js('apiKey', 'texrZgyUTBBb7j0JLGeobQ')
    }

    if (window.location.hostname === 'app.staabserver.com') {
      window.rg4js('apiKey', 'Gyma1L7WdzVAogUbQvQw')
    }

    if (window.location.hostname === 'planfred.lsc360.lu' || window.location.hostname === 'planfred.lsc-group.lu') {
      window.rg4js('apiKey', 'Wdy0cKupdBBBh060hhdGAg')
    }

    window.rg4js('enableCrashReporting', true)
    window.rg4js('enablePulse', false) // real user monitoring
    window.rg4js('filterSensitiveData', raygunSensitiveKeys)
    window.rg4js('setFilterScope', 'all') // Filter any key in the payload
    window.rg4js('setVersion', packageJson.version)
    // logContentsOfXhrCalls is intentionally OFF: it adds the request/response BODY to every XHR
    // breadcrumb as a raw string, and filterSensitiveData cannot redact inside an opaque string —
    // it matches keys. Error reports already carry the parsed+redacted request body via
    // send-raygun.js. The flag does NOT gate the breadcrumb's URL: raygun records that either way,
    // which is what redactRaygunBreadcrumbs in onBeforeSend below takes care of.
    // window.rg4js('logContentsOfXhrCalls', true)

    // aborted axios requests (AbortController / AbortSignal.timeout) are intentional, never report them
    window.rg4js('onBeforeSend', (payload) => {
      if (payload?.Details?.Error?.ClassName === 'CanceledError' || payload?.Details?.Error?.Message === 'canceled') {
        return false
      }

      // Errors with a browser-extension frame come from code we don't ship. This is the extension
      // check of raygun's ignore3rdPartyErrors on its own: the full option also drops every report
      // without a same-origin frame or with an empty stack, which would swallow our warnHandler
      // reports (they send a string) and any sendRaygun call with a non-Error value.
      const stackTrace = payload?.Details?.Error?.StackTrace || []
      const isBrowserExtensionError = stackTrace.some((stackLine) => /^(chrome|moz|safari|safari-web)-extension:\/\//.test(stackLine?.FileName || ''))

      // Safari userscript managers inject code as `user-script:<n>` frames. raygun's parser does not
      // recognise that scheme and drops the frame from StackTrace, so it only survives in StackString.
      const isUserScriptError = /(^|@)user-script:/m.test(payload?.Details?.Error?.StackString || '')

      if (isBrowserExtensionError || isUserScriptError) {
        return false
      }

      // Same for scripts injected into the page from another origin, e.g. a corporate security proxy
      // (Check Point's zerophishing zp.js). This is the same-origin check of ignore3rdPartyErrors,
      // limited to stacks that have frames, so the empty-stack reports above still get through.
      // Frames without a parsable URL or with an opaque origin ([native code], <anonymous>) count as
      // neither side; blob: worker URLs resolve to our origin.
      const frameOrigins = stackTrace
        .map((stackLine) => getUrlOrigin(stackLine?.FileName))
        .filter((origin) => origin && origin !== 'null')
      const isForeignScriptError = frameOrigins.length > 0 && !frameOrigins.includes(window.location.origin)

      if (isForeignScriptError) {
        return false
      }

      // Breadcrumb URLs reach raygun verbatim, credentials included — see the module for the why
      redactRaygunBreadcrumbs(payload)

      // Surface the axios request method/body in raygun's native "HTTP method" and "form values"
      // fields. customData only feeds the custom-data tab, so we copy the request info send-raygun.js
      // attached into Details.Request here. raygun runs customData through merge(), which turns it
      // into a plain object ({0:…} for arrays, or our { axios: … } object), so iterate its values.
      const userCustomData = payload?.Details?.UserCustomData
      const entries = userCustomData && typeof userCustomData === 'object' ? Object.values(userCustomData) : []
      const requestInfo = entries.find((entry) => entry && typeof entry === 'object' && entry.requestMethod)

      if (requestInfo && payload.Details) {
        payload.Details.Request = payload.Details.Request || {}
        payload.Details.Request.HttpMethod = requestInfo.requestMethod.toUpperCase()

        if (requestInfo.requestBody && typeof requestInfo.requestBody === 'object') {
          payload.Details.Request.Form = requestInfo.requestBody
        }
      }

      // Promote the AJAX headline send-raygun.js attached to the error message, so the report is
      // titled and GROUPED by status + method + normalized path. Without this every AxiosError falls
      // back to axios' own message ("Request failed with status code 500"), which collapses every
      // endpoint into a single group. getAjaxErrorMessage strips ids and query credentials for
      // exactly this reason — the URL with its ids intact stays available as
      // customData.axios.requestUrl (only credential query values are redacted there).
      const ajaxErrorMessage = userCustomData?.ajaxErrorMessage

      if (typeof ajaxErrorMessage === 'string' && payload.Details?.Error) {
        payload.Details.Error.Message = ajaxErrorMessage
      }

      return payload
    })

    app.config.warnHandler = function (err, vm, info) {
      window.rg4js('send', {
        error: normalizeError(err),
        customData: [ { info } ],
      })
    }

    app.config.errorHandler = function (err, vm, info) {
      window.rg4js('send', {
        error: normalizeError(err),
        customData: [ { info } ],
      })

      throw err
    }
  },
}
